/**
 * Word document insertion operations via Office.js.
 * @module office/insert
 */

/* global Word */

import { cellPlainText, cellHtml } from "../utils/html";
import { buildTocOoxml, buildCoverPageHtml, insertSbiHeaderFooter, isWordDesktop, insertTocFieldOnline } from "./ooxml";

/**
 * Checks whether the document body contains any text.
 * @returns {Promise<boolean>}
 */
export async function checkDocumentHasContent() {
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load("text");
    await context.sync();
    return body.text && body.text.trim().length > 0;
  });
}

/**
 * Inserts a table into Word using the native Table API.
 * @param {Word.Body} body
 * @param {object} b - Table block data
 */
function insertTableNative(body, b) {
  const sourceRows = (b.rows_rich && b.rows_rich.length) ? b.rows_rich : b.rows;
  if (!sourceRows || !sourceRows.length) return;

  const rowCount = sourceRows.length;
  const colCount = sourceRows.reduce((mx, r) => Math.max(mx, (r || []).length), 0);
  if (colCount === 0) return;

  const values = sourceRows.map(row => {
    const cells = [];
    for (let c = 0; c < colCount; c++) cells.push(cellPlainText((row || [])[c]));
    return cells;
  });

  const table = body.insertTable(rowCount, colCount, Word.InsertLocation.end, values);
  table.font.size = 9;

  if (b.rows_rich && b.rows_rich.length) {
    for (let r = 0; r < rowCount; r++) {
      const row = b.rows_rich[r] || [];
      for (let c = 0; c < colCount; c++) {
        const cell = table.getCell(r, c);
        cell.body.clear();
        cell.body.insertHtml(cellHtml(row[c]), Word.InsertLocation.end);
      }
    }
  }

  try { table.autoFitBehavior = Word.AutoFitBehavior.autoFitToContents; } catch (_) {}

  if (b.has_column_header && rowCount > 0) {
    const headerRow = table.rows.getFirst();
    headerRow.font.bold    = true;
    headerRow.shadingColor = "F0F0F0";
  }
}

/**
 * Inserts an image into Word using the native InlinePicture API.
 * @param {Word.Body} body
 * @param {object} b - Image block data
 * @param {object} imgCache - Block ID → data URI cache
 */
function insertImageNative(body, b, imgCache) {
  const dataUri = imgCache && imgCache[b.id];
  if (!dataUri) return;
  const base64 = dataUri.replace(/^data:[^;]+;base64,/, "");

  const paragraph = body.insertParagraph("", Word.InsertLocation.end);
  paragraph.alignment = Word.Alignment.centered;
  const pic = paragraph.insertInlinePictureFromBase64(base64, Word.InsertLocation.end);

  const WORD_CONTENT_PT = 451;
  const NOTION_CONTENT_PX = 700;

  if (b.width) {
    const notionDisplayPx = Math.min(b.width, NOTION_CONTENT_PX);
    const displayPt = (notionDisplayPx / NOTION_CONTENT_PX) * WORD_CONTENT_PT;
    pic.lockAspectRatio = true;
    pic.width = displayPt;
  } else {
    pic.lockAspectRatio = true;
    pic.width = WORD_CONTENT_PT;
  }

  if (b.caption) {
    const capPara = body.insertParagraph("", Word.InsertLocation.end);
    capPara.alignment = Word.Alignment.centered;
    capPara.insertText(b.caption, Word.InsertLocation.end);
    capPara.font.size   = 9;
    capPara.font.color  = "#888888";
    capPara.font.italic = true;
  }
}

/**
 * Inserts pre-computed content groups into a Word document.
 * @param {object} params
 * @param {Array<Array<object>>} params.groupsData - Pre-computed segments per group
 * @param {Array<{text: string, level: number}>} params.headings - Heading info for style application
 * @param {boolean} params.tocEnabled
 * @param {boolean} params.coverPageEnabled
 * @param {boolean} params.sbiEnabled
 * @param {object|null} params.sbiOptions
 * @param {string|null} params.logoB64
 * @param {string|null} params.bigLogoB64
 * @param {function} params.onProgress - Called with (label, current, total)
 * @param {function} params.checkCancel - Called to check for cancellation
 * @returns {Promise<void>}
 */
export async function insertContent({
  groupsData, headings, tocEnabled, coverPageEnabled, sbiEnabled,
  sbiOptions, logoB64, bigLogoB64, onProgress, checkCancel,
}) {
  await Word.run(async (context) => {
    const body = context.document.body;

    // 1. Vider le document (corps + en-têtes/pieds de page)
    try {
      body.clear();
      await context.sync();
    } catch (e) {
      if (e.code === "GeneralException") {
        throw new Error(
          "Impossible de modifier ce document. Vérifiez qu'il n'est pas protégé ou en mode lecture seule. S'il s'agit d'un fichier récupéré, enregistrez-le d'abord (Fichier > Enregistrer sous) puis réessayez."
        );
      }
      throw e;
    }

    const sections = context.document.sections;
    sections.load("items");
    await context.sync();
    for (const sec of sections.items) {
      sec.getHeader(Word.HeaderFooterType.primary).clear();
      sec.getFooter(Word.HeaderFooterType.primary).clear();
    }
    await context.sync();

    // 2. Insérer le contenu groupe par groupe (1 sync par groupe)
    for (let i = 0; i < groupsData.length; i++) {
      checkCancel();
      onProgress("Insertion", i + 1, groupsData.length);
      for (const seg of groupsData[i]) {
        checkCancel();
        try {
          if (seg.type === "html") {
            if (seg.html) {
              body.insertHtml(seg.html, Word.InsertLocation.end);
              await context.sync();
            }
          } else if (seg.type === "image") {
            insertImageNative(body, seg.block, seg.imgCache);
            await context.sync();
          } else {
            insertTableNative(body, seg.block);
            await context.sync();
          }
        } catch (segErr) {
          // __CANCEL__ doit remonter, pas être avalé
          if (segErr.message === "__CANCEL__") throw segErr;
          console.error(`Erreur segment ${seg.type} dans groupe ${i}:`, segErr.message);
          if (seg.type === "html") console.error("HTML:", seg.html?.substring(0, 500));
          if (segErr.debugInfo) console.error("debugInfo:", JSON.stringify(segErr.debugInfo));
        }
      }
      if (i < groupsData.length - 1) {
        body.insertBreak(Word.BreakType.page, Word.InsertLocation.end);
        await context.sync();
      }
    }

    // 3. Insérer le sommaire en tête (si activé)
    if (tocEnabled) {
      if (isWordDesktop()) {
        body.insertOoxml(buildTocOoxml(), Word.InsertLocation.start);
        await context.sync();
      } else {
        // Word Online : champ TOC natif (seul FieldType supporté sur le web)
        try {
          await insertTocFieldOnline(context, body);
        } catch (tocErr) {
          console.warn("TOC field insertion failed:", tocErr.message);
        }
      }
    }

    // 3b. Page de garde (insérée avant le sommaire, dans sa propre section)
    if (coverPageEnabled && logoB64) {
      onProgress("Page de garde", 1, 1);
      body.insertBreak(Word.BreakType.sectionNext, Word.InsertLocation.start);
      await context.sync();
      body.insertHtml(buildCoverPageHtml(sbiOptions, logoB64, bigLogoB64), Word.InsertLocation.start);
      await context.sync();
    }

    // 4. Appliquer les styles natifs aux titres
    if (headings.length) {
      onProgress("Styles des titres", 1, 1);
      const paras = body.paragraphs;
      context.load(paras, "text");
      await context.sync();

      for (const prefix of ["Titre ", "Heading "]) {
        try {
          let hIdx = 0;
          for (let p = 0; p < paras.items.length && hIdx < headings.length; p++) {
            if (paras.items[p].text.trim() === headings[hIdx].text) {
              paras.items[p].style = prefix + headings[hIdx].level;
              hIdx++;
            }
          }
          await context.sync();
          break;
        } catch (_) { /* essai préfixe suivant */ }
      }
    }

    // 5. En-tête / pied de page SBI
    if (sbiEnabled && sbiOptions) {
      onProgress("En-tête / pied de page", 1, 1);
      await insertSbiHeaderFooter(context, sbiOptions, logoB64, coverPageEnabled);
    }
  });
}
